import {
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { useMutation } from '@cedarjs/web'
import { gql } from '@apollo/client'
import { toast } from 'react-hot-toast'
import { navigate, routes } from '@cedarjs/router'
import PublishIcon from '@mui/icons-material/Publish'
import UnpublishedIcon from '@mui/icons-material/Unpublished'
import CopyIcon from '@mui/icons-material/CopyAll'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { FormTemplate } from 'types/graphql'
import useOrgName from 'src/hooks/useOrgName'
import { useDispatch } from 'src/hooks/useDispatch'
import { openModal } from 'src/slices/modal'
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal'
import useModal from 'src/hooks/useModal'
import hasComponentTypes from 'src/pages/FormTemplatePage/util/hasComponentTypes'

const UPDATE_FORM_TEMPLATE_STATUS_MUTATION = gql`
  mutation UpdateFormTemplateStatus(
    $id: String!
    $input: UpdateFormTemplateInput!
  ) {
    updateFormTemplate(id: $id, input: $input) {
      id
      status
    }
  }
`

const DELETE_FORM_TEMPLATE_MUTATION = gql`
  mutation DeleteFormTemplate($id: String!) {
    deleteFormTemplate(id: $id) {
      id
    }
  }
`

interface SettingsTabProps {
  formTemplate: FormTemplate
  onUpdate: () => void
  onUnsavedChanges?: (hasUnsavedChanges: boolean) => void
}

const SettingsTab = ({
  formTemplate,
  onUpdate,
  onUnsavedChanges,
}: SettingsTabProps) => {
  const orgName = useOrgName()
  const dispatch = useDispatch()

  useModal('confirmationModal', 'change-form-visibility-with-signatures')

  // Local state for form settings
  const [description, setDescription] = useState(formTemplate.description || '')
  const [visibility, setVisibility] = useState<'PUBLIC' | 'ORGANIZATION'>(
    formTemplate.visibility || 'ORGANIZATION'
  )
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Initial values for comparison
  const [initialDescription, setInitialDescription] = useState(
    formTemplate.description || ''
  )
  const [initialVisibility, setInitialVisibility] = useState<
    'PUBLIC' | 'ORGANIZATION'
  >(formTemplate.visibility || 'ORGANIZATION')

  const [updateFormTemplateStatus] = useMutation(
    UPDATE_FORM_TEMPLATE_STATUS_MUTATION
  )
  const [deleteFormTemplate] = useMutation(DELETE_FORM_TEMPLATE_MUTATION)
  const [updateFormTemplate] = useMutation(gql`
    mutation UpdateFormTemplateSettings(
      $id: String!
      $input: UpdateFormTemplateInput!
    ) {
      updateFormTemplate(id: $id, input: $input) {
        id
        description
        visibility
      }
    }
  `)

  // Track changes to form settings
  useEffect(() => {
    const hasChanges =
      description !== initialDescription || visibility !== initialVisibility
    setHasUnsavedChanges(hasChanges)
    onUnsavedChanges?.(hasChanges)
  }, [
    description,
    visibility,
    initialDescription,
    initialVisibility,
    onUnsavedChanges,
  ])

  const handleSave = async () => {
    if (!hasUnsavedChanges) return

    setIsSaving(true)
    try {
      await updateFormTemplate({
        variables: {
          id: formTemplate.id,
          input: {
            description: description.trim() || null,
            visibility,
          },
        },
      })
      setInitialDescription(description)
      setInitialVisibility(visibility)
      setHasUnsavedChanges(false)
      toast.success('Settings saved successfully!')
      onUpdate()
    } catch (error) {
      console.error('Error saving settings:', error)
      toast.error('Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePublishToggle = async () => {
    const newStatus =
      formTemplate.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED'

    try {
      await updateFormTemplateStatus({
        variables: {
          id: formTemplate.id,
          input: { status: newStatus },
        },
      })
      toast.success(
        `Form template ${
          newStatus === 'PUBLISHED' ? 'published' : 'unpublished'
        } successfully`
      )
    } catch (error) {
      console.error('Error updating form template status:', error)
      toast.error('Failed to update form template status')
    }
  }

  const handleCopyLink = () => {
    const link = `${window.location.origin}${routes.submitForm({
      id: formTemplate.id,
    })}`
    navigator.clipboard.writeText(link)
    toast.success('Link copied to clipboard')
  }

  const handleDeleteClick = () => {
    dispatch(
      openModal({
        name: 'confirmationModal',
        data: {
          discriminator: 'delete-form-template',
        },
      })
    )
  }

  const handleDeleteConfirm = async () => {
    try {
      await deleteFormTemplate({ variables: { id: formTemplate.id } })
      toast.success(`"${formTemplate.title}" deleted successfully`)
      navigate(routes.forms({ orgName }))
    } catch (error) {
      console.error('Error deleting form template:', error)
      toast.error('Failed to delete form template')
    }
  }

  // Update local state when formTemplate changes
  useEffect(() => {
    setDescription(formTemplate.description || '')
    setVisibility(formTemplate.visibility || 'ORGANIZATION')
    setInitialDescription(formTemplate.description || '')
    setInitialVisibility(formTemplate.visibility || 'ORGANIZATION')
    setHasUnsavedChanges(false)
  }, [formTemplate])

  return (
    <div className="py-2">
      {/* Form Configuration */}
      <div className="mb-4">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Form Configuration</Typography>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
            startIcon={<SaveIcon />}
            size="small"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
        <div className="max-w-600 flex flex-col gap-3">
          {/* Description Setting */}
          <div>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Description
            </Typography>
            <TextField
              fullWidth
              label="Form Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              size="small"
              multiline
              rows={3}
              placeholder="Describe what this form is for and how it should be used..."
            />
          </div>

          {/* Visibility Setting */}
          <div>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Visibility
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={visibility}
                label="Visibility"
                onChange={(e) => {
                  const newVisibility = e.target.value as
                    | 'PUBLIC'
                    | 'ORGANIZATION'

                  const hasDigitalSignature = hasComponentTypes(
                    formTemplate?.formSchema,
                    ['digitalsignature']
                  )

                  if (newVisibility === 'PUBLIC' && hasDigitalSignature) {
                    dispatch(
                      openModal({
                        name: 'confirmationModal',
                        data: {
                          discriminator:
                            'change-form-visibility-with-signatures',
                        },
                      })
                    )
                  } else {
                    setVisibility(newVisibility)
                  }
                }}
                size="small"
              >
                <MenuItem value="ORGANIZATION">Organization Only</MenuItem>
                <MenuItem value="PUBLIC">Public</MenuItem>
              </Select>
            </FormControl>

            <div className="mt-1">
              {visibility === 'PUBLIC' ? (
                <>
                  <Alert severity="warning" className="mb-1">
                    <Typography variant="body2">
                      <strong>Public forms</strong> can be accessed by anyone
                      with the link, including users outside your organization.
                    </Typography>
                  </Alert>
                  {hasComponentTypes(formTemplate?.formSchema, [
                    'digitalsignature',
                  ]) && (
                    <Alert severity="error" className="mb-1">
                      <Typography variant="body2">
                        <strong>Warning:</strong> This form contains digital
                        signature fields which will not work in public mode.
                        Digital signatures require user authentication which
                        public forms don&apos;t have.
                      </Typography>
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="info" className="mb-1">
                  <Typography variant="body2">
                    <strong>Organization-only forms</strong> can only be
                    accessed by authenticated users within your organization.
                  </Typography>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>

      <Divider className="my-3" />

      {/* Publishing Actions */}
      <div className="mb-4">
        <Typography variant="h6" sx={{ mb: 2 }}>
          Publishing
        </Typography>
        <div className="max-w-600 flex flex-col gap-2">
          <div>
            <Button
              variant="contained"
              color={
                formTemplate.status === 'PUBLISHED' ? 'warning' : 'primary'
              }
              onClick={handlePublishToggle}
              startIcon={
                formTemplate.status === 'PUBLISHED' ? (
                  <UnpublishedIcon />
                ) : (
                  <PublishIcon />
                )
              }
              sx={{ mr: 2 }}
            >
              {formTemplate.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
            </Button>

            {formTemplate.status === 'PUBLISHED' && (
              <Button
                variant="outlined"
                onClick={handleCopyLink}
                startIcon={<CopyIcon />}
              >
                Copy Link
              </Button>
            )}
          </div>

          {formTemplate.status === 'PUBLISHED' ? (
            <Alert severity="success">
              This form is published and can be accessed by users.
            </Alert>
          ) : (
            <Alert severity="info">
              This form is a draft and is not publicly accessible. Publish it to
              make it available to users.
            </Alert>
          )}
        </div>
      </div>

      <Divider className="my-3" />

      {/* Danger Zone */}
      <div>
        <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
          Danger Zone
        </Typography>
        <Card className="border-1 border-error-main">
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Delete Form Template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Once you delete a form template, there is no going back. Please be
              certain.
            </Typography>
            {formTemplate.submissionCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This form has {formTemplate.submissionCount} submission
                {formTemplate.submissionCount !== 1 ? 's' : ''}. You must delete
                the submissions before deleting the form template.
              </Alert>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteClick}
              disabled={formTemplate.submissionCount > 0}
              startIcon={<DeleteIcon />}
            >
              Delete Form Template
            </Button>
          </CardContent>
        </Card>
        <ConfirmationModal
          title="Delete Form Template"
          discriminator="delete-form-template"
          confirmText="Delete"
          denyText="Cancel"
          onConfirm={handleDeleteConfirm}
          richMessage={`Are you sure you want to delete "${formTemplate.title}"? This action cannot be undone.`}
        />
        <ConfirmationModal
          title="Cannot Change Form Visibility"
          discriminator="change-form-visibility-with-signatures"
          confirmText="Ok"
          richMessage={
            'This form contains digital signature fields. Public forms cannot use digital signatures because they require user authentication.\n\n' +
            'You should either:\n' +
            '• Remove the digital signature fields first, or\n' +
            '• Keep the form as Organization-only\n\n'
          }
        />
      </div>
    </div>
  )
}

export default SettingsTab
